import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Form, Button, Container, Card, Modal, Row } from 'react-bootstrap';
import "./AdminLayout.css";
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import BASE_URL from '../config';
// Fix for Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});


// ✅ Create axios instance with Bearer token
const authAxios = axios.create({
  baseURL: BASE_URL,
});

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Reusable components for the map functionality
const mapContainerStyle = {
  height: '400px',
  width: '100%',
};

const defaultCenter = [10.5276, 76.2144];

const geocodeLatLng = async (lat, lng) => {
  try {
    const res = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
    );
    return res.data.display_name || 'Address not found';
  } catch (err) {
    console.error('Geocoding failed:', err);
    return 'Geocoding failed';
  }
};

const MapEvents = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => onMapClick(e.latlng),
  });
  return null;
};

const DraggableMarker = ({ position, onDragEnd }) => {
  const markerRef = useRef(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          onDragEnd(marker.getLatLng());
        }
      },
    }),
    [onDragEnd]
  );
  return <Marker draggable={true} eventHandlers={eventHandlers} position={position} ref={markerRef}></Marker>;
};

// MapPopup component now created internally
const MapPopup = ({ show, handleClose, title, center, setCoordinatesAndAddress }) => {
  const [currentLocation, setCurrentLocation] = useState(center || defaultCenter);
  const [address, setAddress] = useState('');

  useEffect(() => {
    if (show && center) {
      setCurrentLocation(center);
      geocodeLatLng(center[0], center[1]).then(setAddress);
    }
  }, [show, center]);

  const handleMapClick = useCallback(async (latlng) => {
    setCurrentLocation([latlng.lat, latlng.lng]);
    const addr = await geocodeLatLng(latlng.lat, latlng.lng);
    setAddress(addr);
  }, []);

  const handleDragEnd = useCallback(async (latlng) => {
    setCurrentLocation([latlng.lat, latlng.lng]);
    const addr = await geocodeLatLng(latlng.lat, latlng.lng);
    setAddress(addr);
  }, []);

  const handleSelectLocation = () => {
    setCoordinatesAndAddress(currentLocation, address);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <p><b>Coordinates:</b> {currentLocation[0]?.toFixed(4)}, {currentLocation[1]?.toFixed(4)}</p>
          <p><b>Address:</b> {address}</p>
          <Button variant="success" onClick={handleSelectLocation}>Select Location</Button>
        </div>
        <div style={mapContainerStyle}>
          <MapContainer center={currentLocation} zoom={13} style={mapContainerStyle}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <DraggableMarker position={currentLocation} onDragEnd={handleDragEnd} />
            <MapEvents onMapClick={handleMapClick} />
          </MapContainer>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};


const AdminCustomerCreation = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    latitude: '',
    longitude: '',
    externalId: '',
    address: ''
  });
  const [loading, setLoading] = useState(false); // Add a loading state

  const [showMapPopup, setShowMapPopup] = useState(false);
  const [mapCenter, setMapCenter] = useState([10.5276, 76.2144]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectLocation = useCallback((coordinates, address) => {
    setFormData(prevData => ({
      ...prevData,
      latitude: coordinates[0].toString(),
      longitude: coordinates[1].toString(),
      address: address
    }));
    setShowMapPopup(false);
  }, []);

  const handleOpenMap = () => {
    if (formData.latitude && formData.longitude) {
      setMapCenter([parseFloat(formData.latitude), parseFloat(formData.longitude)]);
    } else {
      setMapCenter([10.5276, 76.2144]);
    }
    setShowMapPopup(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      lat: parseFloat(formData.latitude),
      lng: parseFloat(formData.longitude),
      external_id: formData.externalId,
    };

    try {
      const res = await authAxios.post('/customers', payload);
      if (res.data?.success) {
        alert(`Customer created successfully: ${res.data.data.name}`);
        setFormData({
          name: '',
          phone: '',
          email: '',
          latitude: '',
          longitude: '',
          externalId: '',
          address: ''
        });
      } else {
        alert('Failed to create customer.');
        console.error('Response:', res.data);
      }
    } catch (err) {
      console.error('Error creating customer:', err.response || err);
      alert('Error creating customer. Check console for details.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div>
      <Container className="mt-5">
        <Row>
          <div className='col-md-12 col-lg-12' style={{ margin: "0px auto" }}>
            <Card>
              <Card.Body>
                <h3 className="mb-4">Customer Creation</h3>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <div className='col-md-6 col-xs-12 col-lg-6'>
                      <Form.Group controlId="name" className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter name"
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6 col-xs-12 col-lg-6'>
                      <Form.Group controlId="phone" className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter Phone Number"
                          required
                        />
                      </Form.Group>
                    </div>
                  </Row>
                  <Row>
                    <div className='col-md-6 col-xs-12 col-lg-6'>
                      <Form.Group controlId="email" className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="text"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter Email"
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6 col-xs-12 col-lg-6'>
                      <Form.Group controlId="externalId" className="mb-3">
                        <Form.Label>External ID</Form.Label>
                        <Form.Control
                          type="text"
                          name="externalId"
                          value={formData.externalId}
                          onChange={handleChange}
                          placeholder="Enter External ID"
                          required
                        />
                      </Form.Group>
                    </div>
                  </Row>
                  <Row>
                    <div className='col-md-12 col-xs-12 col-lg-12'>
                      <Form.Group controlId="location" className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            name="locationDisplay"
                            value={formData.latitude && formData.longitude ? `${formData.latitude}, ${formData.longitude}` : ''}
                            readOnly
                            placeholder="Select location on map"
                            required
                          />
                          <Button variant="success" onClick={handleOpenMap} className="ms-2">
                            Select on Map
                          </Button>
                        </div>
                      </Form.Group>
                    </div>
                  </Row>
                  <Form.Group controlId="address" className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter address"
                      rows={3}
                      required
                      readOnly
                    />
                  </Form.Group>
                  <Button variant="primary" style={{ width: "100%" }} type="submit" disabled={loading}>
                    {loading ? 'Creating...' : 'Submit'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Row>
        
        {/* Unified Map Popup rendered directly here */}
        <MapPopup
          show={showMapPopup}
          handleClose={() => setShowMapPopup(false)}
          title="Select Customer Location"
          center={mapCenter}
          setCoordinatesAndAddress={handleSelectLocation}
        />
      </Container>
    </div>
  );
};

export default AdminCustomerCreation;